import type { IFuseOptions } from 'fuse.js';
import Fuse from 'fuse.js';
import escapeRegExp from 'lodash/escapeRegExp';
import isNil from 'lodash/isNil';
import map from 'lodash/map';
import size from 'lodash/size';

// Reference: https://www.fusejs.io/examples.html#extended-search
export type SearchKeyword = string | number | undefined;

export type SearchQuery = (keywords: SearchKeyword[]) => string;

export const filterInvalidKeywords = (keywords: SearchKeyword[]): string[] => {
	return keywords
		.filter((key) => !isNil(key))
		.map((key) => (typeof key === 'string' ? escapeRegExp(key) : String(key)));
};

export const queries: Record<string, SearchQuery> = {
	includes: (keywords: SearchKeyword[]) => {
		return ['=', `'`]
			.map((operator) => {
				return keywords.map((key) => `${operator}"${key}"`).join(' | ');
			})
			.join(' | ');
	},
	equals: (keywords: SearchKeyword[]) =>
		`=${keywords.map((key) => `"${key}"`).join(' | ')}`,
};

export const search = <T>(
	list: T[],
	options: IFuseOptions<T>,
	keywords: (string | number | undefined)[],
	query: SearchQuery = queries.includes
) => {
	const fuse = new Fuse<T>(list, {
		useExtendedSearch: true,
		threshold: 0,
		...options,
	});

	const _keywords = filterInvalidKeywords(keywords);
	if (size(_keywords) === 0) {
		return list;
	}

	const results = fuse.search(query(_keywords));
	return map(results, 'item');
};
